import { IParseCreatorsGoogleSheetRequest } from "@shared/lib/types/brief";
import {
  IBriefDeliverableResponse,
  IFieldConfig,
} from "@shared/lib/types/briefInfluencerV2";
import { IDeliverableConfigurator } from "@shared/lib/types/deliverableConfigurator";
import { getPlatformIcon } from "@shared/lib/utils/getPlatformIcon";
import type { TableProps } from "@shared/ui";
import { DeliverablePillSummary, Table } from "@shared/ui";
import { Users } from "lucide-react";
import { useMemo } from "react";
import {
  findBriefDeliverable,
  formatCommercial,
  getCreatorHandle,
  getExtraFieldEntries,
} from "../../../lib/constants";
import styles from "../table.module.scss";

type ProcessedRow = IParseCreatorsGoogleSheetRequest;
type ProcessedColumns = NonNullable<TableProps<ProcessedRow>["columns"]>;

interface ProcessedTableProps {
  rows: ProcessedRow[];
  customFields: IFieldConfig[];
  deliverables: IBriefDeliverableResponse[];
  configurators: IDeliverableConfigurator[];
  onViewErrors: () => void;
}

function getProcessedColumns(
  customFields: IFieldConfig[],
  deliverables: IBriefDeliverableResponse[],
  configurators: IDeliverableConfigurator[]
): ProcessedColumns {
  return [
    {
      title: "Creator",
      key: "creator",
      width: 100,
      render: (_value, row) => (
        <div className={styles.creatorName}>{getCreatorHandle(row)}</div>
      ),
    },
    {
      title: "Platform",
      key: "platform",
      dataIndex: "platform",
      width: 100,
      render: (_value, row) => (
        <span className={styles.platformDisplay}>
          {getPlatformIcon(row.platform)}
          <span className={styles.platformText}>{row.platform || "—"}</span>
        </span>
      ),
    },
    {
      title: (
        <span className={styles.headerTwoLine}>
          Brand
          <br />
          Commercials
        </span>
      ),
      key: "brandCost",
      dataIndex: "brandCost",
      align: "right",
      width: 120,
      render: (_value, row) => (
        <span className={styles.commercialCell}>
          {formatCommercial(row.brandCost)}
        </span>
      ),
    },
    {
      title: (
        <span className={styles.headerTwoLine}>
          Influencer
          <br />
          Commercials
        </span>
      ),
      key: "influencerCost",
      dataIndex: "influencerCost",
      align: "right",
      width: 120,
      render: (_value, row) => (
        <span className={styles.commercialCell}>
          {formatCommercial(row.influencerCost)}
        </span>
      ),
    },
    {
      title: "Deliverables",
      key: "deliverables",
      width: 220,
      render: (_value, row) => {
        const deliverable = findBriefDeliverable(
          row.deliverablesV2Id,
          deliverables
        );
        if (!deliverable) {
          return <span className={styles.emptyValue}>None</span>;
        }
        return (
          <DeliverablePillSummary
            deliverable={deliverable}
            compact
            configurators={configurators}
          />
        );
      },
    },
    {
      title: "Extra fields",
      key: "extraFields",
      width: 180,
      render: (_value, row) => {
        const extras = getExtraFieldEntries(row, customFields);
        if (extras.length === 0) {
          return <span className={styles.emptyValue}>—</span>;
        }
        return extras.map((field) => (
          <div
            key={field.key}
            className={styles.extraLine}
            title={`${field.key}: ${field.value}`}
          >
            <span>{field.key}:</span> {field.value}
          </div>
        ));
      },
    },
  ];
}

export function ProcessedTable({
  rows,
  customFields,
  deliverables,
  configurators,
  onViewErrors,
}: ProcessedTableProps) {
  const columns = useMemo(
    () => getProcessedColumns(customFields, deliverables, configurators),
    [configurators, customFields, deliverables]
  );
  return (
    <Table<ProcessedRow>
      rowKey={(row) => `${row.rowNumber}-${row.socialMediaObjectId}`}
      columns={columns}
      dataSource={rows}
      pagination={false}
      virtual
      scroll={{ x: 800, y: 320 }}
      className={styles.processedTable}
      locale={{
        emptyText: (
          <div className={styles.emptyState}>
            <Users size={28} />
            <p className={styles.emptyTitle}>Nothing ready to add</p>
            <p className={styles.emptySubtitle}>
              Open “Couldn’t import” to see what went wrong.
            </p>
            <button
              type="button"
              className={styles.emptyLink}
              onClick={onViewErrors}
            >
              View failed creators
            </button>
          </div>
        ),
      }}
    />
  );
}
